import PropTypes from 'prop-types'
import { useState } from 'react'
import { Eye, PenLine } from 'lucide-react'

import { CurrencyInput } from '@components/currency-input'
import { FormModal } from '@components/branded-modal'
import { AdasButton, AdasTextField } from '@components/wrapper-components'
import { formatInitialPrice } from '../../../utils/utils'
import { isAutoQuoteProduct } from '../../../utils/productClass'

const TYPE_LABELS = {
  calibration: 'Calibration',
  service: 'Service',
  part: 'Part',
  labor: 'Labor',
  custom: 'Item',
  'charges/discount': 'Charge / Discount'
}

export const EditProductPricing = ({
  type,
  product,
  descriptionLength = 256,
  isOpen,
  onClose,
  handleInputChange,
  onSubmit,
  isNational
}) => {
  const { name, price, description, total_price, quantity } = product
  const isBtnDisabled = !price || !quantity
  const [isValid] = useState({ price: true, quantity: true })

  const handleFieldChange = (e) => {
    const { name, value } = e.target
    handleInputChange({ key: name, value })
  }

  const handleLaborQuantityChange = (e) => {
    const value = e.target.value
    if (/^\d*\.?\d{0,2}$/.test(value)) {
      const numericValue = parseFloat(value)
      if (!isNaN(numericValue) && numericValue > 999.99) return
      handleInputChange({ key: 'quantity', value })
    }
  }

  const handleQuantityChange = (e) => {
    const rawValue = e.target.value
    const value = rawValue.replace(/[^0-9]/g, '').replace(/^0+/, '')
    if (!value) {
      handleInputChange({ key: 'quantity', value: '' })
      return
    }
    const numericValue = parseInt(value, 10)
    if (numericValue <= 999) {
      handleInputChange({ key: 'quantity', value })
    }
  }

  const handleClose = () => onClose(type)
  const isLabor = type === 'labor'
  // National Agreement products are fully read-only for every role (owner/manager/admin/tech):
  // no field is editable and Save is removed — the modal opens as a view-only sheet.
  const viewOnly = !!isNational
  const modalTitle = `${viewOnly ? 'View' : 'Edit'} ${TYPE_LABELS[type] || 'Item'}`
  const priceDisabled = viewOnly
  // Auto Quote line items are generated — only price is editable from the QWI.
  const isAutoQuote = isAutoQuoteProduct(product)
  const quantityDisabled = isAutoQuote || viewOnly
  const remaining = descriptionLength - (description?.length || 0)

  const footer = (
    <div className='flex items-center justify-end gap-3'>
      <AdasButton variant='outlined' onClick={handleClose} className='rounded-lg px-5'>
        {viewOnly ? 'CLOSE' : 'CANCEL'}
      </AdasButton>
      {!viewOnly && (
        <AdasButton
          type='submit'
          disabled={isBtnDisabled}
          variant='contained'
          onClick={() => onSubmit({ type, product })}
          className='rounded-lg bg-[#0A1628] px-5 text-white hover:bg-[#1F2D3D]'
        >
          SAVE
        </AdasButton>
      )}
    </div>
  )

  return (
    <FormModal open={isOpen} onClose={handleClose} title={modalTitle} icon={viewOnly ? Eye : PenLine} footer={footer}>
      <AdasTextField
        onChange={handleInputChange}
        disabled
        name='name'
        id='name'
        label='Name'
        variant='outlined'
        value={name || ''}
        fullWidth
        InputLabelProps={{ shrink: true }}
        inputProps={{ maxLength: 100 }}
        className='mb-5'
      />

      <CurrencyInput
        value={formatInitialPrice(price)}
        error={!isValid.price}
        label={isLabor ? 'Hourly Rate' : 'Unit Price'}
        id={isLabor ? 'hourlyRate' : 'unitPrice'}
        name='price'
        onChange={handleFieldChange}
        fullWidth
        variant='outlined'
        InputLabelProps={{ shrink: true }}
        disabled={priceDisabled}
        className='mb-5'
      />

      <AdasTextField
        onChange={isLabor ? handleLaborQuantityChange : handleQuantityChange}
        disabled={quantityDisabled}
        name='quantity'
        id={isLabor ? 'hours' : 'quantity'}
        label={isLabor ? 'Hours' : 'Quantity'}
        variant='outlined'
        value={quantity ?? ''}
        error={!isValid.quantity}
        fullWidth
        InputLabelProps={{ shrink: true }}
        className='mb-5'
      />

      <CurrencyInput
        value={formatInitialPrice(total_price)}
        label='Total Price'
        id='totalPrice'
        name='totalPrice'
        onChange={handleInputChange}
        fullWidth
        disabled
        variant='outlined'
        InputLabelProps={{ shrink: true }}
        className='mb-5'
      />

      <AdasTextField
        fullWidth
        name='description'
        label='Description'
        placeholder='Add a description for this line item...'
        value={description || ''}
        onChange={handleFieldChange}
        inputProps={{ maxLength: descriptionLength }}
        helperText={`Maximum ${descriptionLength} characters (${remaining} remaining).`}
        multiline
        rows={4}
        disabled={viewOnly || isAutoQuote}
        InputLabelProps={{ shrink: true }}
      />
    </FormModal>
  )
}

EditProductPricing.propTypes = {
  type: PropTypes.string.isRequired,
  title: PropTypes.string,
  product: PropTypes.shape({
    id: PropTypes.string,
    itemNum: PropTypes.number,
    name: PropTypes.string,
    price: PropTypes.number,
    description: PropTypes.string,
    totalPrice: PropTypes.number,
    quantity: PropTypes.number,
    item_type: PropTypes.string
  }).isRequired,
  descriptionLength: PropTypes.number,
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isNational: PropTypes.bool
}
